import { useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ColorPicker } from "./ColorPicker";
import { toast } from "sonner";

interface AddToCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationIds: string[];
  onComplete?: () => void;
}

export function AddToCollectionDialog({
  open,
  onOpenChange,
  locationIds,
  onComplete,
}: AddToCollectionDialogProps) {
  const [selectedId, setSelectedId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");

  const utils = trpc.useUtils();
  const { data: collections, isLoading } = trpc.collections.list.useQuery(
    {},
    { enabled: open },
  );

  const createMutation = trpc.collections.create.useMutation({
    onSuccess: (data) => {
      utils.collections.list.invalidate();
      setSelectedId(data.id);
      setShowCreate(false);
      setNewName("");
    },
    onError: (err) => toast.error(err.message),
  });

  const addMutation = trpc.collections.addLocations.useMutation({
    onSuccess: () => {
      utils.collections.list.invalidate();
      toast.success(
        `Added ${locationIds.length} location${locationIds.length > 1 ? "s" : ""} to collection`,
      );
      onComplete?.();
      onOpenChange(false);
      setSelectedId("");
    },
    onError: (err) => toast.error(err.message),
  });

  const handleAdd = () => {
    if (!selectedId || locationIds.length === 0) return;
    addMutation.mutate({ collectionId: selectedId, locationIds });
  };

  const handleCreateAndAdd = () => {
    if (!newName.trim()) return;
    createMutation.mutate({ name: newName.trim(), color: newColor });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Add {locationIds.length} Location
            {locationIds.length > 1 ? "s" : ""} to Collection
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search collections..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              <RadioGroup value={selectedId} onValueChange={setSelectedId} className="max-h-60 overflow-y-auto">
                {(collections ?? []).filter((col: any) =>
                  col.name.toLowerCase().includes(searchQuery.toLowerCase())
                ).map((col: any) => (
                  <label
                    key={col.id}
                    className="flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <RadioGroupItem value={col.id} />
                    <span
                      className="h-3 w-3 rounded-full shrink-0"
                      style={{ backgroundColor: col.color }}
                    />
                    <span className="flex-1 text-sm font-medium">
                      {col.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {col.locationCount} locations
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </>
          )}

          {!showCreate ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowCreate(true)}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Create New Collection
            </Button>
          ) : (
            <div className="space-y-3 border rounded-md p-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Name</Label>
                <Input
                  placeholder="Collection name..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Color</Label>
                <ColorPicker value={newColor} onChange={setNewColor} />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreate(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={!newName.trim() || createMutation.isPending}
                  onClick={handleCreateAndAdd}
                >
                  {createMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  )}
                  Create
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!selectedId || addMutation.isPending}
          >
            {addMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            )}
            Add to Collection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
